import { useContext } from 'react';
import { useSearchParams } from 'react-router';

import { UserContext } from '~/auth/userContext';
import { ContractEditDialog } from '~/components/contracts/form/ContractEditDialog';
import type { ContractFormSection } from '~/components/contracts/form/ContractFormDialog';
import { editingContractSectionKey } from '~/components/global/constants';
import { canAccess, EDIT_CONTRACT } from '~/utils/role';

type Props = {
  editingContractId: string | null;
  onContractEdit: (id: string | null) => void;
};

/**
 * The contract edit form, mounted once at `_app.tsx` and driven by
 * `?editingContractId=`. App-wide rather than route-owned because the contract page,
 * the contracts list and the Work Queue all open it through that param.
 */
export const ContractEditDialogFlow = ({ editingContractId, onContractEdit }: Props) => {
  const { userDetails } = useContext(UserContext);
  const [searchParams] = useSearchParams();

  // This flow is mounted app-shell-wide, so without a role check
  // `?editingContractId=<uuid>` on any route opens the edit form for a client
  // (AMP-482). `ContractViewSet` enforces the same rule server-side.
  const canEdit = canAccess(userDetails.role, EDIT_CONTRACT);

  if (!canEdit || !editingContractId) return null;

  return (
    <ContractEditDialog
      contractId={editingContractId}
      onClose={() => onContractEdit(null)}
      // Unvalidated on purpose: the dialog falls back to General for anything
      // it doesn't recognise, so a stale or hand-edited URL opens the form
      // rather than failing.
      initialSection={
        (searchParams.get(editingContractSectionKey) as ContractFormSection | null) ?? undefined
      }
    />
  );
};
